import { Injector } from '@angular/core';
import { NavigationItem } from '../../models/common';
import { ClaimService } from '../../services/claim/claim.service';
import { FeatureFlagService } from '../../services/feature-flag/feature-flag.service';
import { getUrlFromRouterLink } from '../../utilities/route.helpers';
import { Atlas, AtlasNode } from './atlas';

export interface SimplePathSegment {
    id: string;
    path: string;
}

export type SimplePath = SimplePathSegment[];

export function getNavigationGroup(
    configuration: Atlas,
    navigationGroup: string,
    options?: { absoluteLink: boolean }
): NavigationItem[] {
    // Make configurable if need arises.
    const depthForChildren = 1;

    const flattenConfig = (atlas: Atlas): Atlas => {
        const _flattenedConfig = {} as Atlas;
        const flattenConfigRecursively = (_atlas: Atlas): void => {
            Object.keys(_atlas).forEach((key) => {
                _flattenedConfig[key] = _atlas[key];
                if (_atlas[key].children) {
                    flattenConfigRecursively(_atlas[key].children);
                }
            });
        };
        flattenConfigRecursively(atlas);
        return _flattenedConfig;
    };

    const flattenedConfig = flattenConfig(configuration);
    const filteredIds = Object.keys(flattenedConfig).filter(
        (id) =>
            flattenedConfig[id].navigationGroups &&
            flattenedConfig[id].navigationGroups.some((group) => group === navigationGroup)
    );

    let levelsDeep = 0;
    const rebuildTree = (atlas: Atlas, ids: string[]): NavigationItem[] =>
        ids.map((id) => {
            const node = getNodeById(atlas, id);
            const navItem =
                options && options.absoluteLink
                    ? getAbsoluteNavigationItemForNode(atlas, id)
                    : getNavigationItemForNode(atlas, id);
            if (node.children && levelsDeep < depthForChildren) {
                levelsDeep++;
                navItem.children = rebuildTree(node.children, Object.keys(node.children));
            }
            return navItem;
        });
    return rebuildTree(configuration, filteredIds);
}

export function getNavigationItemForNode(
    atlas: Atlas,
    nodeId: string,
    options: Partial<NavigationItem> = {}
): NavigationItem {
    try {
        const node = getNodeById(atlas, nodeId);
        const routerLink = getRouterLinkById(atlas, nodeId, { relative: true });
        return new NavigationItem({
            label: node.title,
            routerLink: routerLink,
            routerLinkUrl: getUrlFromRouterLink(routerLink),
            claims: node.claims,
            environments: node.environments,
            featureFlags: node.featureFlags,
            allowAnonymous: nodeAllowsAnonymous(atlas, nodeId),
            ...options
        });
    } catch (error) {
        console.error('Could not find a node matching that ID.');
        console.error(`nodeId: ${nodeId}`);
        throw error;
    }
}

export function getAbsoluteNavigationItemForNode(
    atlas: Atlas,
    nodeId: string,
    options: Partial<NavigationItem> = {}
): NavigationItem {
    return {
        ...getNavigationItemForNode(atlas, nodeId, options),
        routerLink: getRouterLinkById(atlas, nodeId)
    };
}

export function getNavigationItemsForAtlas(
    atlas: Atlas,
    relativeToUrl = '',
    sharedOptions: Partial<NavigationItem> = {}
): NavigationItem[] {
    return Object.keys(atlas).map((nodeId) => {
        const node = getNodeById(atlas, nodeId);
        const routerLink = getRouterLinkById(atlas, nodeId, { relative: true });
        return new NavigationItem({
            label: node.title,
            routerLink: !!relativeToUrl ? [relativeToUrl, ...routerLink] : routerLink,
            routerLinkUrl: relativeToUrl + getUrlFromRouterLink(routerLink),
            claims: node.claims,
            environments: node.environments,
            featureFlags: node.featureFlags,
            allowAnonymous: nodeAllowsAnonymous(atlas, nodeId),
            ...sharedOptions
        });
    });
}

export function getNodeById(atlas: Atlas, id: string): AtlasNode | undefined {
    if (!atlas) {
        throw new Error(
            'getRouteConfig received a falsy value for the "atlas" parameter.\n' +
                'atlas: ' +
                atlas +
                '\n' +
                'id: ' +
                id
        );
    }
    return Object.keys(atlas).reduce((acc, testId) => {
        let newNode = acc;
        if (!newNode) {
            if (id === testId) {
                newNode = atlas[testId];
            } else if (atlas[testId] && !!atlas[testId].children) {
                newNode = getNodeById(atlas[testId].children, id);
            }
        }
        return newNode;
    }, atlas[id]);
}

export function getSimplePathsForAtlas(atlas: Atlas): SimplePath[] {
    const paths = [] as SimplePath[];
    function collect(_atlas: Atlas, prev?: SimplePath) {
        for (const key of Object.keys(_atlas)) {
            if (typeof _atlas[key] !== 'object') {
                continue;
            }
            prev = prev || [];
            const val = _atlas[key];
            const path: SimplePath = [...prev, { id: key, path: val.path }];

            if (val.children) {
                collect(val.children, path);
            } else {
                paths.push(path);
            }
        }
    }
    collect(atlas);
    return paths;
}

/**
 * @param {boolean} options.relative Set to `true` to avoid adding a leading slash.
 * Defaults to `false`, meaning that a leading slash will be added to the resulting `routerLink`.
 * @example
 * const atlas = {
 *     FOO: {
 *         path: 'foo',
 *         children: {
 *             BAR: { path: 'bar' }
 *         }
 *     }
 * }
 * getRouterLinkById(atlas, 'BAR'); // returns ['/', 'foo', 'bar']
 * getRouterLinkById(atlas, 'BAR', { relative: true }); // returns ['foo', 'bar']
 * getRouterLinkById(atlas, 'BAR', { noStandaloneLeadingSlash: true }); // returns ['/foo', 'bar']
 */
export function getRouterLinkById(
    atlas: Atlas,
    id: string,
    options?: { relative?: boolean; noStandaloneLeadingSlash?: boolean }
): string[] {
    if (!atlas) {
        throw new Error(
            'getRouterLinkById received a falsy value for the "atlas" parameter.\n' +
                'atlas: ' +
                atlas +
                '\n' +
                'id: ' +
                id
        );
    }
    const { relative = false, noStandaloneLeadingSlash = false } = options || {};
    const paths = getSimplePathsForAtlas(atlas);
    const pathWithId = paths.find((path) => path.findIndex((segment) => segment.id === id) > -1);
    const pathToId = pathWithId.slice(0, pathWithId.findIndex((segment) => segment.id === id) + 1);
    // Filtering out empty strings, since Router treats them each as a `/`.
    let routerLink = pathToId.map(({ path }) => path).filter((element) => !!element);
    if (!relative) {
        if (noStandaloneLeadingSlash) {
            const [firstSegment, ...restOfSegments] = routerLink;
            routerLink = ['/' + firstSegment, ...restOfSegments];
        } else {
            routerLink = ['/', ...routerLink];
        }
    }
    return routerLink;
}

export function getRouterLinkUrlById(
    atlas: Atlas,
    id: string,
    options?: { relative: boolean }
): string {
    return getUrlFromRouterLink(getRouterLinkById(atlas, id, options), options);
}

export function nodeAllowsAnonymous(atlas: Atlas, nodeId: string): boolean {
    return getNodeById(atlas, nodeId).allowAnonymous;
}

export function getFirstActiveRouterLink(atlas: Atlas, injector: Injector): string[] {
    const nodeIds = Object.keys(atlas);
    for (let i = 0; i < nodeIds.length; i++) {
        const node = getNodeById(atlas, nodeIds[i]);
        if (canActivateNode(node, injector)) {
            return getRouterLinkById(atlas, nodeIds[i]);
        }
    }
    return [];
}

export function canActivateNode(node: AtlasNode, injector: Injector): boolean {
    const claimService = injector.get(ClaimService);
    const featureFlagService = injector.get(FeatureFlagService);
    let validClaim: boolean;
    let validFeatureFlag: boolean;
    let validEnvironment: boolean;

    if (!!node.claims) {
        validClaim = node.claims.some((claim) => claimService.isOnSync(claim));
    } else {
        validClaim = true;
    }

    if (!!node.featureFlags) {
        validFeatureFlag = node.featureFlags.every((featureFlag) =>
            featureFlagService.isOnSync(featureFlag)
        );
    } else {
        validFeatureFlag = true;
    }

    if (!!node.environments) {
        validEnvironment = node.environments.some(
            (environment) => this._settings.environment === environment
        );
    } else {
        validEnvironment = true;
    }

    return validClaim && validFeatureFlag && validEnvironment;
}

export function getNodeByUrl(atlas: Atlas, url: string): AtlasNode | undefined {
    const nodes = getNodesByUrl(atlas, url);
    return nodes[nodes.length - 1];
}

export function getNodesByUrl(atlas: Atlas, url: string): AtlasNode[] {
    const paths = url.split('/');
    const nodes = [] as AtlasNode[];
    const getLastNode = () => nodes[nodes.length - 1];
    const getNodeAtPath = (_atlas: Atlas, path: string) =>
        (
            (_atlas &&
                Object.keys(_atlas)
                    .map((id) => ({ id, node: _atlas[id] }))
                    .find(({ id }) => _atlas[id].path === path)) || { node: undefined }
        ).node;
    paths.forEach((path, index) => {
        const lastNode = getLastNode();
        const _atlas = index === 0 ? atlas : lastNode && lastNode.children;
        if (_atlas && getNodeAtPath(_atlas, path)) {
            nodes.push(getNodeAtPath(_atlas, path));
        }
    });
    return nodes;
}
